import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { GameBoard } from '../components/GameBoard'
import { AlreadyPlayedView } from '../components/AlreadyPlayedView'
import { isValidMode } from '../utils/validation'
import {
  trackPageView,
  trackPerformance,
  trackFeatureDiscovery,
} from '../utils/analytics'
import {
  hasPerformanceSupport,
  getFirstContentfulPaint,
  measureTimeToInteractive,
  getPerformanceNavigationTiming,
} from '../utils/performanceUtils'
import { motion } from 'framer-motion'
import { useGameContext } from '../contexts/GameContext'
import { trackEvent } from '../services/hotjar'
import { useGameSettings } from '../contexts/GameSettingsContext'
import { WordLength } from '../types/game'
import { hasModeBeenPlayed } from '../utils/storage'
import { GAME_MODES } from '../constants/gameConfig'

const GamePage: React.FC = () => {
  const { mode } = useParams<{ mode: string }>()
  const navigate = useNavigate()
  const { isLoading, initGame } = useGameContext()
  const { updateSettings } = useGameSettings()
  const [error, setError] = useState<string | null>(null)
  const [alreadyPlayed, setAlreadyPlayed] = useState(false)
  const [availableModes, setAvailableModes] = useState<WordLength[]>([])
  const modeValue = mode || '5' // Default to 5 if mode is undefined

  // Memoize performance tracking to avoid creating function on each render
  const trackLoadTimePerformance = useCallback(() => {
    if (!hasPerformanceSupport() || !mode) return

    const navigationTiming = getPerformanceNavigationTiming()
    const fcp = getFirstContentfulPaint()
    const tti = measureTimeToInteractive()

    trackPerformance({
      metric: 'page_load',
      value:
        navigationTiming?.loadEventEnd ||
        Date.now() -
          ((window as Window & { pageStartTime?: number }).pageStartTime || 0),
      page: 'game',
      gameMode: mode,
    })

    if (fcp) {
      trackPerformance({
        metric: 'first_contentful_paint',
        value: fcp,
        page: 'game',
        gameMode: mode,
      })
    }

    trackPerformance({
      metric: 'time_to_interactive',
      value: tti,
      page: 'game',
      gameMode: mode,
    })
  }, [mode])

  useEffect(() => {
    // Set page start time for performance tracking
    ;(window as Window & { pageStartTime?: number }).pageStartTime = Date.now()

    // Validate game mode
    if (!mode || !isValidMode(mode)) {
      setError(`Invalid game mode: ${mode}`)
      navigate('/zword')
      return
    }

    // Parse mode to number
    const modeNumber = parseInt(mode, 10) as WordLength
    if (
      !isNaN(modeNumber) &&
      (modeNumber === 3 ||
        modeNumber === 4 ||
        modeNumber === 5 ||
        modeNumber === 6)
    ) {
      updateSettings({ selectedMode: modeNumber })

      // Check if user has already played this mode today
      if (hasModeBeenPlayed(modeNumber)) {
        setAlreadyPlayed(true)

        // Find other modes that haven't been played yet
        const unplayedModes = Object.keys(GAME_MODES)
          .map(Number)
          .filter(
            (m): m is WordLength =>
              (m === 3 || m === 4 || m === 5 || m === 6) &&
              !hasModeBeenPlayed(m as WordLength)
          )
          .sort((a, b) => a - b)

        setAvailableModes(unplayedModes)

        // Track attempt to replay completed mode
        trackEvent('attempted_replay_completed_mode', {
          mode: modeNumber,
          available_modes: unplayedModes.join(','),
        })
      } else {
        // Clear stale state from a previously visited already-played mode
        setAlreadyPlayed(false)
        setAvailableModes([])
        // Initialize game with the specified mode
        initGame(mode)
      }
    }

    // Track page view
    trackPageView({
      page: 'game',
      gameMode: mode,
    })

    // Track Hotjar game page view
    trackEvent('game_page_viewed', {
      game_mode: mode,
      timestamp: Date.now(),
    })

    // Track performance metrics after a short delay to ensure values are available
    const performanceTimer = setTimeout(trackLoadTimePerformance, 1000)

    // Track feature discovery
    trackFeatureDiscovery({
      feature: 'game_board_loaded',
      gameMode: mode,
    })

    return () => {
      clearTimeout(performanceTimer)
    }
  }, [mode, navigate, initGame, trackLoadTimePerformance, updateSettings])

  // Handle mode selection
  const handleModeSelect = (selectedMode: WordLength) => {
    updateSettings({ selectedMode })
    navigate(`/zword/play/${selectedMode}`)

    // Track mode selection from already-played screen
    trackEvent('mode_selected_from_already_played', {
      previous_mode: modeValue,
      selected_mode: selectedMode.toString(),
    })
  }

  // Handle back to modes
  const handleBackToModes = () => {
    navigate('/zword')

    trackEvent('back_to_modes_from_already_played', {
      from_mode: modeValue,
    })
  }

  // Calculate next UTC midnight for the reset time message
  const calculateResetTime = () => {
    const now = new Date()
    const tomorrow = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    )

    // Convert UTC time to local time for display
    const localDate = new Date(tomorrow.getTime())
    const hours = localDate.getHours()
    const minutes = localDate.getMinutes()

    // Convert to 12-hour format
    const hour12 = hours % 12 || 12
    const ampm = hours >= 12 ? 'pm' : 'am'

    // Add leading zero to minutes if needed
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes

    return `${hour12}:${minutesStr}${ampm}`
  }

  const resetTimeStr = calculateResetTime()

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  // If already played, show the AlreadyPlayedView component
  if (alreadyPlayed) {
    return (
      <AlreadyPlayedView
        mode={modeValue}
        availableModes={availableModes}
        resetTimeStr={resetTimeStr}
        onModeSelect={handleModeSelect}
        onBackToModes={handleBackToModes}
      />
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      data-hj-allow="true" // Allow Hotjar to record this component
    >
      <div className="game-header">
        <h1 className="game-title">{`${modeValue}-Letter Challenge`}</h1>
      </div>

      <div className="container">
        <div className="game-container" data-hj-gamemode={mode}>
          {isLoading ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <GameBoard />
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default GamePage
